/**
 * Persistent folder sort-mode preference (manual / alphabetical / updated).
 * Backed by localStorage so the user's choice survives reloads.
 */

import { useCallback, useEffect, useState } from "react"

import type { FolderSortMode } from "../utils/folder-tree"

const STORAGE_KEY = "slide-sense.folder-sort-mode"
const DEFAULT_MODE: FolderSortMode = "manual"

function readStored(): FolderSortMode {
  if (typeof window === "undefined") return DEFAULT_MODE
  const v = window.localStorage.getItem(STORAGE_KEY)
  if (v === "manual" || v === "alphabetical" || v === "updated") return v
  return DEFAULT_MODE
}

export function useFolderSortMode(): [FolderSortMode, (mode: FolderSortMode) => void] {
  const [mode, setMode] = useState<FolderSortMode>(readStored)

  const update = useCallback((next: FolderSortMode) => {
    setMode(next)
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next)
    }
  }, [])

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      // React to every event for our key, not just truthy newValue.
      // Another tab might clear or invalid-set the key; in that case we
      // want to fall back to the default (readStored handles both cases)
      // instead of leaving this tab stuck on the prior value.
      if (e.key === STORAGE_KEY) {
        setMode(readStored())
      }
    }
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [])

  return [mode, update]
}
